import React, { useState, useEffect } from "react";
import AdminSideMenu from "../../components/AdminSideMenu";
import api from "../../utils/axiosInstance";
import { useAuth } from "../../contexts/AuthContext";
import * as XLSX from "xlsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Loading Component
const LoadingScreen = () => (
  <div className="flex min-h-screen bg-gray-100">
    <AdminSideMenu isMenuOpen={false} setIsMenuOpen={() => {}} />
    <div className="flex-1 p-6 md:ml-64 flex items-center justify-center">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
        <h2 className="text-xl font-semibold text-gray-700">
          Loading Reports...
        </h2>
        <p className="text-gray-500 mt-2">
          Please wait while we fetch the data
        </p>
      </div>
    </div>
  </div>
);

// Error Component
const ErrorScreen = ({ error, onRetry }) => (
  <div className="flex min-h-screen bg-gray-100">
    <AdminSideMenu isMenuOpen={false} setIsMenuOpen={() => {}} />
    <div className="flex-1 p-6 md:ml-64 flex items-center justify-center">
      <div className="text-center bg-white rounded-xl shadow p-8 max-w-md">
        <div className="text-red-500 text-5xl mb-4">⚠️</div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Failed to Load Data
        </h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <button
          onClick={onRetry}
          className="bg-blue-500 text-white px-6 py-2 rounded shadow hover:bg-blue-600"
        >
          Retry
        </button>
      </div>
    </div>
  </div>
);

function isDateInRange(check, from, to) {
  if (!check) return false;
  const d = typeof check === "string" ? new Date(check) : check;

  const fromDate = from ? new Date(from) : null;
  const toDate = to ? new Date(to) : null;

  if (toDate) {
    toDate.setHours(23, 59, 59, 999);
  }

  if (fromDate && d < fromDate) return false;
  if (toDate && d > toDate) return false;

  return true;
}

function getTodayStr() {
  const today = new Date();
  const yyyy = today.getFullYear();
  const mm = String(today.getMonth() + 1).padStart(2, "0");
  const dd = String(today.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

const DEFAULT_START = "2025-01-01";
const DEFAULT_END = getTodayStr();

const exportCsv = (rows, label) => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  const blob = new Blob([csv], { type: "text/csv" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${label}_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

const exportXlsx = (rows, label, sheet = "Report") => {
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, sheet);
  XLSX.writeFile(wb, `${label}_${new Date().toISOString().slice(0, 10)}.xlsx`);
};

const Reports = () => {
  const { userRole } = useAuth();
  const [activeTab, setActiveTab] = useState("summary");
  const [carriers, setCarriers] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [pendingDrivers, setPendingDrivers] = useState([]);
  const [interests, setInterests] = useState([]);
  const [dateRange, setDateRange] = useState({
    carriers: [DEFAULT_START, DEFAULT_END],
    subCarriers: [DEFAULT_START, DEFAULT_END],
    notSubCarriers: [DEFAULT_START, DEFAULT_END],
    deactCarriers: [DEFAULT_START, DEFAULT_END],
    allDrivers: [DEFAULT_START, DEFAULT_END],
    fleetyDrivers: [DEFAULT_START, DEFAULT_END],
    manualDrivers: [DEFAULT_START, DEFAULT_END],
    allottedDrivers: [DEFAULT_START, DEFAULT_END],
    favoriteDrivers: [DEFAULT_START, DEFAULT_END],
    summaryCarriers: [DEFAULT_START, DEFAULT_END],
    summaryDrivers: [DEFAULT_START, DEFAULT_END],
    summaryPackages: [DEFAULT_START, DEFAULT_END],
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Role-based permissions
  const canViewReport = (reportType) => {
    if (userRole === "SUPER_ADMIN" || userRole === "MAIN_ADMIN") {
      return true;
    }

    const permissions = {
      MARKETING_ADMIN: {
        summaryCarriers: true,
        summaryPackages: false,
        notSubscribed: true,
        deactivated: true,
      },
      ACCOUNTS_ADMIN: {
        summaryPackages: true,
        subscribed: true,
        notSubscribed: true,
        deactivated: true,
      },
    };

    return permissions[userRole]?.[reportType] || false;
  };

  // Check if tab should be visible
  const canViewTab = (tabName) => {
    if (userRole === "SUPER_ADMIN" || userRole === "MAIN_ADMIN") {
      return true;
    }

    if (tabName === "summary") {
      return (
        canViewReport("summaryCarriers") ||
        canViewReport("summaryDrivers") ||
        canViewReport("summaryPackages")
      );
    }

    if (tabName === "carriers") {
      return (
        canViewReport("notSubscribed") ||
        canViewReport("deactivated") ||
        canViewReport("subscribed")
      );
    }

    if (tabName === "drivers") {
      return userRole === "SUPER_ADMIN" || userRole === "MAIN_ADMIN";
    }

    return false;
  };

  const range = (key) => dateRange[key] || [DEFAULT_START, DEFAULT_END];

  const filterListByDate = (list, key, field = "createdAt") => {
    const [from, to] = range(key);
    if (!from && !to) return list;
    let actualField = field;
    if (
      field === "createdAt" &&
      list &&
      list.length &&
      list[0] &&
      typeof list[0] === "object" &&
      "createAt" in list[0]
    ) {
      actualField = "createAt";
    }
    return list.filter((item) => {
      let dateVal = item[actualField] || item.createdAt || item.createAt;
      if (!dateVal) return true;
      return isDateInRange(dateVal, from, to);
    });
  };

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      setError(null);

      try {
        const [
          carriersRes,
          driversRes,
          subscriptionsRes,
          pendingDriversRes,
          interestsRes,
          packagesRes,
        ] = await Promise.all([
          api.get("/carriers"),
          api.get("/drivers"),
          api.get("/packageSubscribed/all-subscriptions"),
          api.get("/pendingdrivers"),
          api.get("/interests"),
          api.get("/packages"),
        ]);

        let fetchedCarriers = [];
        if (Array.isArray(carriersRes.data)) {
          fetchedCarriers = carriersRes.data;
        } else if (carriersRes.data?.data) {
          fetchedCarriers = Array.isArray(carriersRes.data.data)
            ? carriersRes.data.data
            : [];
        } else if (carriersRes.data?.carriers) {
          fetchedCarriers = Array.isArray(carriersRes.data.carriers)
            ? carriersRes.data.carriers
            : [];
        }

        let fetchedDrivers = [];
        if (Array.isArray(driversRes.data)) {
          fetchedDrivers = driversRes.data;
        } else if (driversRes.data?.data) {
          fetchedDrivers = Array.isArray(driversRes.data.data)
            ? driversRes.data.data
            : [];
        } else if (driversRes.data?.drivers) {
          fetchedDrivers = Array.isArray(driversRes.data.drivers)
            ? driversRes.data.drivers
            : [];
        }

        let fetchedSubscriptions = [];
        if (Array.isArray(subscriptionsRes.data)) {
          fetchedSubscriptions = subscriptionsRes.data;
        } else if (subscriptionsRes.data?.data) {
          fetchedSubscriptions = Array.isArray(subscriptionsRes.data.data)
            ? subscriptionsRes.data.data
            : [];
        }

        const fetchedPendingDrivers = Array.isArray(pendingDriversRes.data)
          ? pendingDriversRes.data
          : pendingDriversRes.data?.data || [];

        const fetchedInterests = Array.isArray(interestsRes.data)
          ? interestsRes.data
          : interestsRes.data?.data || [];

        const fetchedPackages = Array.isArray(packagesRes.data)
          ? packagesRes.data
          : packagesRes.data?.data || [];

        setCarriers(fetchedCarriers);
        setDrivers(fetchedDrivers);
        setSubscriptions(fetchedSubscriptions);
        setPendingDrivers(fetchedPendingDrivers);
        setInterests(fetchedInterests);
        setPackages(fetchedPackages);
      } catch (error) {
        console.error("❌ Error fetching data:", error);
        setError(error.message);
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  // Set initial tab based on permissions
  useEffect(() => {
    const availableTabs = ["summary", "carriers", "drivers"].filter(canViewTab);
    if (availableTabs.length > 0 && !canViewTab(activeTab)) {
      setActiveTab(availableTabs[0]);
    }
  }, [userRole]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    const yyyy = date.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };

  const formatCarrierForExport = (carrier) => ({
    CompanyName: carrier.companyName || "N/A",
    FleetyID: carrier.identificationNumber || "N/A",
    ContactName: carrier.contactName || "N/A",
    JobTitle: carrier.jobTitle || "N/A",
    Email: carrier.email || "N/A",
    Phone: carrier.phone || "N/A",
    Address: carrier.address || "N/A",
    City: carrier.city || "N/A",
    State: carrier.state || "N/A",
    ZIP: carrier.zip || "N/A",
    Website: carrier.website || "N/A",
    CompanyDesc: carrier.companyDesc || "N/A",
    Trailer: Array.isArray(carrier.trailer)
      ? carrier.trailer.join(", ")
      : carrier.trailer || "N/A",
    Status: carrier.status || (carrier.isActive ? "Active" : "Inactive"),
    RegisteredDate: formatDate(carrier.createdAt || carrier.createAt),
  });

  const allCarriersData = carriers.map(formatCarrierForExport);
  const registeredCarriers = filterListByDate(carriers, "carriers").map(
    formatCarrierForExport
  );

  const carriersSubscribed = filterListByDate(subscriptions, "subCarriers")
    .map((sub) => {
      const carrier = carriers.find(
        (c) => String(c._id) === String(sub.carrierId)
      );
      const pkg = packages.find((p) => String(p._id) === String(sub.packageId));

      return {
        CarrierName: carrier?.companyName || "N/A",
        FleetyID: carrier?.identificationNumber || "N/A",
        PhoneNo: carrier?.phone || "N/A",
        PackageName: pkg?.name || "N/A",
        Amount: pkg?.price || 0,
        Status:
          sub.status && String(sub.status).toLowerCase() === "active"
            ? "Active"
            : "Inactive",
        SubscriptionDate: formatDate(sub.createdAt),
        AutoPayment: sub.autoPayment ? "Yes" : "No",
      };
    })
    .filter(
      (item) =>
        item.CarrierName !== "N/A" &&
        item.FleetyID !== "N/A" &&
        item.PhoneNo !== "N/A" &&
        item.PackageName !== "N/A" &&
        item.Amount > 0
    );

  const subscribedCarrierIds = new Set(
    subscriptions
      .filter((s) => s.carrierId)
      .map((s) => String(s.carrierId?._id || s.carrierId))
  );

  const notSubscribedCarriers = carriers
    .filter((c) => !subscribedCarrierIds.has(String(c._id)))
    .map((carrier) => {
      const filtered = filterListByDate([carrier], "notSubCarriers");
      return filtered.length > 0 ? formatCarrierForExport(carrier) : null;
    })
    .filter((c) => c !== null);

  const deactivatedCarriers = carriers
    .filter((c) => c.status === "Inactive")
    .map((carrier) => {
      const filtered = filterListByDate([carrier], "deactCarriers");
      return filtered.length > 0 ? formatCarrierForExport(carrier) : null;
    })
    .filter((c) => c !== null);

  const formatDriverForExport = (driver) => ({
    Name: driver.name || "N/A",
    FleetyID: driver.identificationNumber || "N/A",
    Email: driver.email || "N/A",
    Phone: driver.phone || "N/A",
    Route: Array.isArray(driver.route)
      ? driver.route.join(", ")
      : driver.route || "N/A",
    JobType: driver.job || "N/A",
    Trailer: Array.isArray(driver.trailer)
      ? driver.trailer.join(", ")
      : driver.trailer || "N/A",
    CDL: driver.cdl || "N/A",
    Endorsements: Array.isArray(driver.endorsements)
      ? driver.endorsements.join(", ")
      : driver.endorsements || "N/A",
    City: driver.city || "N/A",
    State: driver.state || "N/A",
    Address: driver.address || "N/A",
    ZIP: driver.zip || "N/A",
    Experience: driver.experience || "N/A",
    CreatedBy:
      driver.createdBy === "admin"
        ? "Created by Admin"
        : driver.createdBy === "fleety"
        ? "Registered through portal"
        : driver.createdBy === "upload"
        ? "Added via file upload"
        : "N/A",
    RegisteredDate: formatDate(driver.createdAt),
  });

  const allDrivers = filterListByDate(drivers, "allDrivers").map(
    formatDriverForExport
  );

  const fleetyDriversFiltered = drivers.filter((d) => {
    return d.createdBy && String(d.createdBy).toLowerCase() === "fleety";
  });

  const fleetyDrivers = filterListByDate(
    fleetyDriversFiltered,
    "fleetyDrivers"
  ).map(formatDriverForExport);

  const manualDrivers = filterListByDate(
    drivers.filter((d) => {
      const createdBy = d.createdBy ? String(d.createdBy).toLowerCase() : "";
      return createdBy === "admin" || createdBy === "upload";
    }),
    "manualDrivers"
  ).map(formatDriverForExport);

  const allottedDrivers = filterListByDate(
    drivers.filter((d) => d.assignedCarrier),
    "allottedDrivers",
    "assignedAt"
  ).map((driver) => {
    const carrier = carriers.find(
      (c) => String(c._id) === String(driver.assignedCarrier)
    );
    return {
      DriverName: driver.name || "N/A",
      DriverFleetyID: driver.identificationNumber || "N/A",
      DriverPhone: driver.phone || "N/A",
      AssignedCarrier: carrier?.companyName || "N/A",
      CarrierFleetyID: carrier?.identificationNumber || "N/A",
      CarrierPhone: carrier?.phone || "N/A",
      AssignedDate: formatDate(driver.assignedAt),
    };
  });

  const favoriteDrivers = filterListByDate(
    interests,
    "favoriteDrivers",
    "date"
  ).map((interest) => {
    const driver = drivers.find(
      (d) => String(d._id) === String(interest.driverId)
    );
    const carrier = carriers.find(
      (c) => String(c._id) === String(interest.carrierId)
    );
    return {
      DriverName: driver?.name || "N/A",
      DriverFleetyID: driver?.identificationNumber || "N/A",
      CarrierName: carrier?.companyName || "N/A",
      CarrierFleetyID: carrier?.identificationNumber || "N/A",
      Favoriting: formatDate(interest.date),
    };
  });

  const packageStats = packages.map((pkg) => {
    const packageSubscriptions = subscriptions.filter(
      (s) => String(s.packageId) === String(pkg._id)
    );
    const subscriptionCount = packageSubscriptions.length;
    const revenue = subscriptionCount * (pkg.price || 0);

    return {
      name: pkg.name || "N/A",
      count: subscriptionCount,
      price: pkg.price || 0,
      total: revenue,
      duration: pkg.duration || "N/A",
      features: Array.isArray(pkg.features)
        ? pkg.features.join(", ")
        : pkg.features || "N/A",
    };
  });

  const packageReportRows = packages.map((pkg) => {
    const packageSubscriptions = subscriptions.filter(
      (s) => String(s.packageId) === String(pkg._id)
    );
    const subscriptionCount = packageSubscriptions.length;
    const revenue = subscriptionCount * (pkg.price || 0);

    return {
      PackageName: pkg.name || "N/A",
      Price: pkg.price || 0,
      Duration: pkg.duration || "N/A",
      Features: Array.isArray(pkg.features)
        ? pkg.features.join(", ")
        : pkg.features || "N/A",
      TotalSubscriptions: subscriptionCount,
      TotalRevenue: revenue,
    };
  });

  function blockHeader({ title, info, handlerXlsx, handlerCsv, rangeKey }) {
    const [from, to] = range(rangeKey);

    if (isLoading) {
      return <LoadingScreen />;
    }

    if (error) {
      return (
        <ErrorScreen error={error} onRetry={() => window.location.reload()} />
      );
    }

    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
        {info && <div className="text-gray-500 text-xs mb-2">{info}</div>}
        <div className="flex flex-col md:flex-row md:items-end md:justify-end gap-2 mb-4">
          <div className="flex items-center gap-2">
            <DatePicker
              selected={from ? new Date(from) : null}
              onChange={(date) =>
                setDateRange((r) => ({
                  ...r,
                  [rangeKey]: [
                    date ? date.toISOString().split("T")[0] : "",
                    r[rangeKey][1],
                  ],
                }))
              }
              dateFormat="MM/dd/yyyy"
              placeholderText="From"
              className="border px-3 py-1 rounded"
              maxDate={to ? new Date(to) : null}
            />
            <span className="text-gray-600">to</span>
            <DatePicker
              selected={to ? new Date(to) : null}
              onChange={(date) =>
                setDateRange((r) => ({
                  ...r,
                  [rangeKey]: [
                    r[rangeKey][0],
                    date ? date.toISOString().split("T")[0] : "",
                  ],
                }))
              }
              dateFormat="MM/dd/yyyy"
              placeholderText="To"
              className="border px-3 py-1 rounded"
              minDate={from ? new Date(from) : null}
            />
          </div>
          <div className="flex gap-1">
            <button
              onClick={handlerCsv}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow"
            >
              CSV
            </button>
            <button
              onClick={handlerXlsx}
              className="bg-blue-500 text-white px-4 py-2 rounded shadow"
            >
              EXCEL
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) return <LoadingScreen />;
  if (error)
    return (
      <ErrorScreen error={error} onRetry={() => window.location.reload()} />
    );

  return (
    <div className="flex min-h-screen bg-gray-100">
      <AdminSideMenu isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />
      <div className="flex-1 p-6 md:ml-64">
        <h1 className="text-3xl font-bold mb-6">Reports</h1>
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex mb-6 border-b">
            {["summary", "carriers", "drivers"]
              .filter(canViewTab)
              .map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 font-semibold border-b-4 mr-4 ${
                    activeTab === tab
                      ? "border-blue-700 text-blue-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
          </div>

          {/* SUMMARY TAB */}
          {activeTab === "summary" && (
            <div>
              {canViewReport("summaryCarriers") && (
                <>
                  <section className="mb-8">
                    {blockHeader({
                      title: "Carrier Report",
                      info: `Total Carriers: ${
                        filterListByDate(carriers, "summaryCarriers").length
                      }`,
                      handlerXlsx: () =>
                        exportXlsx(
                          filterListByDate(carriers, "summaryCarriers").map(
                            formatCarrierForExport
                          ),
                          "Carriers_Report"
                        ),
                      handlerCsv: () =>
                        exportCsv(
                          filterListByDate(carriers, "summaryCarriers").map(
                            formatCarrierForExport
                          ),
                          "Carriers_Report"
                        ),
                      rangeKey: "summaryCarriers",
                    })}
                    <div className="bg-gray-50 rounded-lg p-6 flex flex-col items-center shadow">
                      <span className="text-gray-500 text-sm font-medium mb-2">
                        Total Registered Carriers
                      </span>
                      <span className="text-2xl font-bold text-gray-900">
                        {filterListByDate(carriers, "summaryCarriers").length}
                      </span>
                    </div>
                  </section>
                  <hr className="my-8 border-gray-300" />
                </>
              )}

              {canViewReport("summaryDrivers") && (
                <>
                  <section className="mb-8">
                    {blockHeader({
                      title: "Drivers Report",
                      info: `Total Drivers: ${
                        filterListByDate(drivers, "summaryDrivers").length
                      }`,
                      handlerXlsx: () =>
                        exportXlsx(
                          filterListByDate(drivers, "summaryDrivers").map(
                            formatDriverForExport
                          ),
                          "Drivers Report"
                        ),
                      handlerCsv: () =>
                        exportCsv(
                          filterListByDate(drivers, "summaryDrivers").map(
                            formatDriverForExport
                          ),
                          "Drivers Report"
                        ),
                      rangeKey: "summaryDrivers",
                    })}
                    <div className="bg-gray-50 rounded-lg p-6 flex flex-col items-center shadow">
                      <span className="text-gray-500 text-sm font-medium mb-2">
                        Total Registered Drivers
                      </span>
                      <span className="text-2xl font-bold text-gray-900">
                        {filterListByDate(drivers, "summaryDrivers").length}
                      </span>
                    </div>
                  </section>
                  <hr className="my-8 border-gray-300" />
                </>
              )}

              {canViewReport("summaryPackages") && (
                <section>
                  {blockHeader({
                    title: "Package Report",
                    handlerXlsx: () =>
                      exportXlsx(packageReportRows, "Packages Report"),
                    handlerCsv: () =>
                      exportCsv(packageReportRows, "Packages Report"),
                    rangeKey: "summaryPackages",
                  })}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {packageStats.map((p, i) => (
                      <div
                        key={i}
                        className="bg-white rounded-xl p-5 shadow flex flex-col items-center border border-gray-200"
                      >
                        <span className="text-sm text-gray-700 font-semibold mb-1">
                          {p.name}
                        </span>
                        <span className="text-xs text-gray-400 mb-1">
                          Packages Subscribed
                        </span>
                        <span className="text-xl font-bold">{p.count}</span>
                        <span className="text-xs text-gray-700">
                          Duration:{" "}
                          <span className="font-semibold">{p.duration}</span>
                        </span>
                        <span className="text-xs text-gray-700">
                          Price:{" "}
                          <span className="font-semibold">${p.price}</span>
                        </span>
                        <span className="text-xs text-gray-700">
                          Revenue:{" "}
                          <span className="font-medium text-primary">
                            ${p.total}
                          </span>
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}

          {/* CARRIERS TAB */}
          {activeTab === "carriers" && (
            <div>
              {canViewReport("registered") && (
                <>
                  <section>
                    {blockHeader({
                      title: "List of Registered Carriers",
                      info: `Total: ${registeredCarriers.length} carriers`,
                      handlerXlsx: () =>
                        exportXlsx(registeredCarriers, "Registered_Carriers"),
                      handlerCsv: () =>
                        exportCsv(registeredCarriers, "Registered_Carriers"),
                      rangeKey: "carriers",
                    })}
                  </section>
                  <hr className="my-8 border-gray-200" />
                </>
              )}

              {canViewReport("subscribed") && (
                <>
                  <section>
                    {blockHeader({
                      title: "Carriers Subscribed to Packages",
                      info: `Total: ${carriersSubscribed.length} subscriptions`,
                      handlerXlsx: () =>
                        exportXlsx(
                          carriersSubscribed,
                          "Subscribed Carriers Report"
                        ),
                      handlerCsv: () =>
                        exportCsv(
                          carriersSubscribed,
                          "Subscribed Carriers Report"
                        ),
                      rangeKey: "subCarriers",
                    })}
                  </section>
                  <hr className="my-8 border-gray-200" />
                </>
              )}

              {canViewReport("notSubscribed") && (
                <>
                  <section>
                    {blockHeader({
                      title: "Carriers Not Subscribed to Any Package",
                      info: `Total: ${notSubscribedCarriers.length} carriers`,
                      handlerXlsx: () =>
                        exportXlsx(
                          notSubscribedCarriers,
                          "Carriers_Not_Subscribed"
                        ),
                      handlerCsv: () =>
                        exportCsv(
                          notSubscribedCarriers,
                          "Carriers_Not_Subscribed"
                        ),
                      rangeKey: "notSubCarriers",
                    })}
                  </section>
                  <hr className="my-8 border-gray-200" />
                </>
              )}

              {canViewReport("deactivated") && (
                <section>
                  {blockHeader({
                    title: "List of Deactivated Carriers",
                    info: `Total: ${deactivatedCarriers.length} carriers`,
                    handlerXlsx: () =>
                      exportXlsx(deactivatedCarriers, "Deactivated_Carriers"),
                    handlerCsv: () =>
                      exportCsv(deactivatedCarriers, "Deactivated_Carriers"),
                    rangeKey: "deactCarriers",
                  })}
                </section>
              )}
            </div>
          )}

          {/* DRIVERS TAB */}
          {activeTab === "drivers" && canViewTab("drivers") && (
            <div>
              <section>
                {blockHeader({
                  title: "List of All Drivers",
                  info: `Total: ${allDrivers.length} drivers`,
                  handlerXlsx: () => exportXlsx(allDrivers, "All_Drivers"),
                  handlerCsv: () => exportCsv(allDrivers, "All_Drivers"),
                  rangeKey: "allDrivers",
                })}
              </section>
              <hr className="my-8 border-gray-200" />
              <section>
                {blockHeader({
                  title: "List of Drivers Registered through online",
                  info: `Total: ${fleetyDrivers.length} drivers`,
                  handlerXlsx: () =>
                    exportXlsx(fleetyDrivers, "Registered Drivers Report"),
                  handlerCsv: () =>
                    exportCsv(fleetyDrivers, "Registered Drivers Report"),
                  rangeKey: "fleetyDrivers",
                })}
              </section>
              <hr className="my-8 border-gray-200" />
              <section>
                {blockHeader({
                  title: "List of Drivers Uploaded & Added Manually",
                  info: `Total: ${manualDrivers.length} drivers`,
                  handlerXlsx: () =>
                    exportXlsx(manualDrivers, "Added Drivers Report"),
                  handlerCsv: () =>
                    exportCsv(manualDrivers, "Added Drivers Report"),
                  rangeKey: "manualDrivers",
                })}
              </section>
              <hr className="my-8 border-gray-200" />
              <section>
                {blockHeader({
                  title: "List of Drivers Allotted to Carriers",
                  info: `Total: ${allottedDrivers.length} assignments`,
                  handlerXlsx: () =>
                    exportXlsx(allottedDrivers, "Drivers_Allotted_To_Carriers"),
                  handlerCsv: () =>
                    exportCsv(allottedDrivers, "Drivers_Allotted_To_Carriers"),
                  rangeKey: "allottedDrivers",
                })}
              </section>
              <hr className="my-8 border-gray-200" />
              <section>
                {blockHeader({
                  title: "List of Drivers Added to Favorites",
                  info: `Total: ${favoriteDrivers.length} favorites`,
                  handlerXlsx: () =>
                    exportXlsx(favoriteDrivers, "Drivers_Added_To_Favorites"),
                  handlerCsv: () =>
                    exportCsv(favoriteDrivers, "Drivers_Added_To_Favorites"),
                  rangeKey: "favoriteDrivers",
                })}
              </section>
            </div>
          )}
        </div>
      </div>
      {isMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default Reports;
